import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import './css/footer.css';
import logo from './images/logo.png';
import Img1 from './images/allimages/1.webp';
import Img2 from './images/allimages/2.webp';
import Img3 from './images/allimages/3.webp';
import Img4 from './images/allimages/4.webp';
import Img5 from './images/allimages/5.webp';
import Img6 from './images/allimages/6.webp';
import { useLanguage } from '../contexts/LanguageContext';
import { useApiContent } from '../hooks/useApiContent';

const FooterSection = () => {
  const { t } = useLanguage();
  const { getText } = useApiContent();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    loadSocialLinks();
  }, []);

  const loadSocialLinks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/social-media`);
      const data = await response.json();
      if (data.success) {
        setSocialLinks(data.data);
      }
    } catch (error) {
      console.error('Error loading social links:', error);
    }
  };

  const getSocialIcon = (platform) => {
    const icons = {
      facebook: Facebook,
      instagram: Instagram,
      linkedin: Linkedin,
      twitter: Twitter
    };
    return icons[platform.toLowerCase()] || Facebook;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setEmail('');
  };

  // Handle gallery click - navigate to home page (portfolio section is there)
  const handleGalleryClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      // Scroll to portfolio section after navigation
      setTimeout(() => {
        const portfolioSection = document.querySelector('.portfolio-section');
        if (portfolioSection) {
          portfolioSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const portfolioSection = document.querySelector('.portfolio-section');
      if (portfolioSection) {
        portfolioSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div className="footer-wrapper">
      
      {/* Newsletter Section */}
      <div className="newsletter-section">
        <div className="newsletter-content">
          <h2 className="newsletter-title">
            {t('footer.newsletterTitle')}
          </h2>
          <form className="newsletter-form" onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder={t('footer.newsletterPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="newsletter-input"
              required
            />
            <button type="submit" className="newsletter-button">
              {t('footer.newsletterButton')}
            </button>
          </form>
        </div>
      </div>

      {/* Main Footer Section */}
      <div className="footer-main">
        <div className="footer-container">
          
          {/* Company Info */}
          <div className="footer-column">
            <div className="company-logo">
              <img src={logo} alt="Company Logo" />
              <div className="company-name">
                <h3>{t('footer.companyName')}</h3>
                <p>{t('footer.companySubtitle')}</p>
              </div>
            </div>

            <p className="company-description">
              {t('footer.companyDescription')}
            </p>

            <Link to="/about" className="discover-button">{t('footer.discoverMore')}</Link>
          </div>

          {/* Quick Links */}
          <div className="footer-column">
            <h4 className="footer-heading">{t('footer.quickLinks')}</h4>
            <ul className="footer-links">
              <li><Link to="/about">{t('footer.links.about')}</Link></li>
              <li><Link to="/why-choose-us">{t('footer.links.vision')}</Link></li>
              <li><a href="/" onClick={handleGalleryClick}>{t('footer.links.projects')}</a></li>
              <li><a href="mailto:info@morasalat.com">{t('footer.links.careers')}</a></li>
              <li><Link to="/contact">{t('footer.links.contact')}</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div className="footer-column">
            <h4 className="footer-heading">{t('footer.services')}</h4>
            <ul className="footer-links">
              <li><Link to="/service/1">{t('footer.links.engineering')}</Link></li>
              <li><Link to="/service/2">{t('footer.links.construction')}</Link></li>
              <li><Link to="/service/4">{t('footer.links.infrastructure')}</Link></li>
              <li><Link to="/service/3">{t('footer.links.design')}</Link></li>
              <li><Link to="/service/5">{t('footer.links.development')}</Link></li>
            </ul>
          </div>

          {/* Gallery */}
          <div className="footer-column">
            <h4 className="footer-heading">{t('footer.gallery')}</h4>
            <div className="gallery-grid">
              <Link to="/gallery" onClick={handleGalleryClick}>
                <img src={Img1} alt="Project 1" />
              </Link>
              <Link to="/gallery" onClick={handleGalleryClick}>
                <img src={Img2} alt="Project 2" />
              </Link>
              <Link to="/gallery" onClick={handleGalleryClick}>
                <img src={Img3} alt="Project 3" />
              </Link>
              <Link to="/gallery" onClick={handleGalleryClick}>
                <img src={Img4} alt="Project 4" />
              </Link>
              <Link to="/gallery" onClick={handleGalleryClick}>
                <img src={Img5} alt="Project 5" />
              </Link>
              <Link to="/gallery" onClick={handleGalleryClick}>
                <img src={Img6} alt="Project 6" />
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Footer */}
      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <p className="copyright">
            {getText('footer', 'copyright') || t('footer.copyright')}
          </p>

          <div className="social-icons">
            {socialLinks.map((link) => {
              const Icon = getSocialIcon(link.platform);
              return (
                <a 
                  key={link.id} 
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label={link.platform}
                >
                  <Icon size={20} />
                </a>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
};

export default FooterSection;
